import { useCallback, useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"

import { api } from "~/utils/api"

/**
 * Resolves the Forecast Updates page for a set of variants and returns a
 * navigate callback. Forecast Updates lives per noos collection, so the link
 * targets the collection most of the given variants belong to; `canOpen` is
 * false when none of them are in a noos collection.
 */
export function useForecastUpdatesLink(variantIds: string[], enabled = true) {
  const navigate = useNavigate()
  const { data: membership } = api.collection.getNoosCollectionMembershipForVariants.useQuery(
    { variantIds },
    { enabled: enabled && variantIds.length > 0 },
  )

  const collectionId = useMemo(() => {
    if (!membership?.length) return null
    const counts = new Map<number, number>()
    for (const m of membership) counts.set(m.collectionId, (counts.get(m.collectionId) ?? 0) + 1)
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]![0]
  }, [membership])

  const openForecastUpdates = useCallback(() => {
    if (collectionId === null) return
    void navigate({
      to: "/demand-planning/noos/$collectionId/forecast-updates",
      params: { collectionId: String(collectionId) },
    })
  }, [navigate, collectionId])

  return { canOpen: collectionId !== null, openForecastUpdates }
}
